"use client";

import Tooltip from "@/components/ui/tooltip";

import { getCycleColor } from "@/lib/care-plan-cycle-colors";
import { cx } from "@/lib/tailwind-utils";

import type { CarePlanCycleDetail } from "@/types/care-plan";

// Definitions, not patient iterations: templates retain their member hierarchy.
export function TemplateCycleBadge({
  cycle,
  colorIndex,
}: {
  cycle: CarePlanCycleDetail;
  colorIndex: number;
}) {
  const color = getCycleColor(colorIndex);
  const minutes = cycle.gapMinutes;
  const [amount, unit] =
    minutes % 1440 === 0
      ? [minutes / 1440, "day"]
      : minutes % 60 === 0
        ? [minutes / 60, "hour"]
        : [minutes, "minute"];
  const gap = `${amount} ${unit}${amount === 1 ? "" : "s"}`;
  const badgeLabel = `${cycle.name?.trim() || `Cycle ${colorIndex + 1}`} · ${cycle.passCount} ${cycle.passCount === 1 ? "pass" : "passes"} · ${gap} between passes`;
  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-caption-strong",
        color.bg,
        color.text,
      )}
    >
      {badgeLabel}
    </span>
  );
}

interface CycleBadgeProps {
  cyclePass: number;
  cycleName?: string | null;
  colorIndex: number;
  className?: string;
}

export function CycleBadge({
  cyclePass,
  cycleName,
  colorIndex,
  className,
}: CycleBadgeProps) {
  const color = getCycleColor(colorIndex);
  const label = cycleName?.trim()
    ? cycleName.trim()
    : `Cycle ${colorIndex + 1}`;

  const badge = (
    <span
      className={cx(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-caption-strong whitespace-nowrap",
        color.bg,
        color.text,
        className,
      )}
    >
      <span className="max-w-32 truncate">{label}</span>
      <span>&middot; Pass {cyclePass}</span>
    </span>
  );

  return (
    <Tooltip content={label} triggerAsChild>
      {badge}
    </Tooltip>
  );
}
